import { Badge, Banner, FlexBox, Text } from '@dixa/lib-shared-habla';
import { SourceConversationsChip } from 'lib-knowledge-utils/components/sourceConversations/index';
import { routeGenerator } from 'lib-knowledge-utils/routes';

import {
  gapTypeLabel,
  JudgeOutcome,
  KnowledgeGap,
  skipReasonText,
} from './testRun.utils';

export function TestRunOutcome({
  outcome,
}: {
  outcome: JudgeOutcome;
}): JSX.Element {
  switch (outcome.__typename) {
    case 'KnowledgeGapJudgeOutcome_Skipped':
      return (
        <Banner
          variant="info"
          headline="The conversation was skipped"
          description={
            <Text.Paragraph as="span" variant="3">
              {skipReasonText(outcome.reason)}
            </Text.Paragraph>
          }
        />
      );

    case 'KnowledgeGapJudgeOutcome_NotEnoughSimilarConversations': {
      const found = outcome.similarConversationIds.length;
      return (
        <Banner
          variant="info"
          headline="Not enough similar conversations"
          description={
            <FlexBox flexDirection="column" gap="200" alignItems="flex-start">
              <Text.Paragraph as="span" variant="3">
                {`The AI found ${found} similar ${found === 1 ? 'conversation' : 'conversations'} and needs ${outcome.required} before it checks for a gap.`}
              </Text.Paragraph>
              {found > 0 && (
                <SourceConversationsChip
                  conversations={outcome.similarConversationIds.map((csid) => ({
                    csid,
                  }))}
                />
              )}
            </FlexBox>
          }
        />
      );
    }

    case 'KnowledgeGapJudgeOutcome_Judged':
      if (outcome.gaps.length === 0) {
        return (
          <Banner
            variant="success"
            headline="No knowledge gaps found"
            description={
              <Text.Paragraph as="span" variant="3">
                The knowledge base already covers this conversation.
              </Text.Paragraph>
            }
          />
        );
      }
      return (
        <GapList
          title={
            outcome.gaps.length === 1
              ? 'The AI found 1 gap'
              : `The AI found ${outcome.gaps.length} gaps`
          }
          gaps={outcome.gaps}
        />
      );
  }
}

export function GapList({
  title,
  gaps,
  withLinks = true,
}: {
  title: string;
  gaps: readonly KnowledgeGap[];
  withLinks?: boolean;
}): JSX.Element {
  return (
    <FlexBox flexDirection="column" gap="200">
      <Text.Paragraph variant="1">{title}</Text.Paragraph>
      <FlexBox as="ul" flexDirection="column" gap="200" margin="0" padding="0">
        {gaps.map((gap, index) => (
          <GapItem key={index} gap={gap} withLink={withLinks} />
        ))}
      </FlexBox>
    </FlexBox>
  );
}

function GapItem({ gap, withLink }: { gap: KnowledgeGap; withLink: boolean }) {
  const link = !withLink
    ? undefined
    : gap.articleId
      ? {
          href: routeGenerator.articleEdit({ articleId: gap.articleId }),
          label: 'Open article',
        }
      : gap.aiDraftId
        ? {
            href: `${routeGenerator.articleSuggestions()}?suggestionId=${encodeURIComponent(gap.aiDraftId)}`,
            label: 'Open suggestion',
          }
        : undefined;

  return (
    <FlexBox as="li" flexDirection="column" gap="100">
      <FlexBox alignItems="center" gap="200">
        <Badge size="small" variant="secondary">
          {gapTypeLabel(gap.gapType)}
        </Badge>
        {link && (
          <Text.ExternalLink href={link.href} target="_blank" rel="noreferrer">
            {link.label}
          </Text.ExternalLink>
        )}
      </FlexBox>
      {gap.gapDescription && (
        <Text.Paragraph variant="3">{gap.gapDescription}</Text.Paragraph>
      )}
    </FlexBox>
  );
}
